import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_options,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options,
} from '@zg-rentals/rental-manager-env';

import { Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useUpdateArchiveStatus } from '../../../hooks/queries/properties';

import PropertiesActions from '../../../actions/PropertiesActions';

import propertyUtils from '../../../utils/propertyUtils';

import ModalFooter from '../../../components/Modal/ModalFooter';

import type { RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = { onClose: () => void; isArchived: boolean; listingAlias: string; address: RentalManagerAddressDto };

const ArchivePropertyModal = ({ onClose, isArchived, listingAlias, address }: Props) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    ...address,
  });
  const [hasArchivingError, setHasArchivingError] = useState(false);

  const bodyText = isArchived
    ? `${formattedCompleteAddress} will be restored to your Properties page.`
    : `If you no longer own or manage ${formattedCompleteAddress}, archive it to remove it from your properties.`;
  const linkText = isArchived ? 'for rent & off market' : 'archived';
  const headerText = isArchived ? 'Remove property from archive?' : 'When to archive a property';

  const { mutate: updateArchiveStatus } = useUpdateArchiveStatus(listingAlias, isArchived, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
      dispatch(PropertiesActions.updateCurrentProperty({ isArchived: !isArchived }));

      // Invalidate all properties/list queries
      queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
      queryClient.invalidateQueries({
        queryKey: useRentalManagerAccountViewZramMultifamilyWebApiHandler_options.getKey({ listingAlias }),
      });
      queryClient.invalidateQueries({
        queryKey: useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options.getKey({ listingAlias }),
      });
      onClose();
    },
    onError: () => {
      setHasArchivingError(true);
    },
  });

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2} textStyle="heading-xs">{`${isArchived ? 'Unarchive' : 'Archive'} property`}</Heading>}
      body={
        <div data-test="modal-body">
          <Heading level={4}>{headerText}</Heading>
          <Paragraph data-test="body-text" css={{ marginBlock: '400' }}>
            {bodyText}
          </Paragraph>
          {!isArchived && (
            <Paragraph data-test="isNotArchivedMessage" css={{ marginBottom: '400' }}>
              You&#39;re still able to access the property by viewing
              {` ${linkText} `}
              properties from your Properties page.
            </Paragraph>
          )}
        </div>
      }
      footer={
        <ModalFooter
          submitText={isArchived ? 'Unarchive' : 'Archive'}
          onSubmit={updateArchiveStatus}
          onCancel={() => {
            setHasArchivingError(false);
            onClose();
          }}
          hasError={hasArchivingError}
        />
      }
    />
  );
};

export default ArchivePropertyModal;
